/**
 * Admin Settings Page
 * Tabbed: General | Payment Gateway | Notifications (future)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CogIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL || 'https://api.shunrindo.com/api/v1';

function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  const tenantId = localStorage.getItem('tenantId');
  return {
    Authorization: `Bearer ${token}`,
    'X-Tenant-ID': tenantId || '1'
  };
}

const TABS = [
  { id: 'general', label: 'General', icon: CogIcon },
  { id: 'payment', label: 'Payment Gateway', icon: CreditCardIcon }
];

/* ─────────────────────────────────────────────
   General Tab
───────────────────────────────────────────── */
function GeneralTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: getAuthHeaders() })
      .then(res => setSettings(res.data.settings))
      .catch(() => setMessage({ type: 'error', text: 'Failed to load settings' }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.patch(`${API}/admin/settings`, {
        businessName: settings.businessName,
        contactEmail: settings.contactEmail,
        contactPhone: settings.contactPhone,
        timezone: settings.timezone,
        currency: settings.currency
      }, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: 'Settings saved successfully' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;

  return (
    <div className="max-w-xl space-y-5">
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="w-4 h-4" /> : <ExclamationCircleIcon className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {[
        { key: 'businessName', label: 'Business Name' },
        { key: 'contactEmail', label: 'Contact Email', type: 'email' },
        { key: 'contactPhone', label: 'Contact Phone' },
        { key: 'timezone', label: 'Timezone' },
        { key: 'currency', label: 'Currency (ISO 4217)' }
      ].map(field => (
        <div key={field.key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
          <input
            type={field.type || 'text'}
            value={settings?.[field.key] || ''}
            onChange={e => setSettings(s => ({ ...s, [field.key]: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      ))}

      <div className="pt-2">
        <div className="text-sm text-gray-500 mb-3">
          <span className="font-medium">Subscription:</span> {settings?.subscriptionStatus}
          {settings?.trialEndsAt && ` · Trial ends ${new Date(settings.trialEndsAt).toLocaleDateString('en-GB')}`}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Payment Gateway Tab
───────────────────────────────────────────── */
function PaymentGatewayTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings/payment`, { headers: getAuthHeaders() });
      setConfig(res.data);
      setSelectedGateway(res.data.gateway || null);
      setIsLive(res.data.isLive || false);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load payment settings' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      if (!selectedGateway) {
        // Remove gateway
        await axios.post(`${API}/admin/settings/payment`, { gateway: null }, { headers: getAuthHeaders() });
        setMessage({ type: 'success', text: 'Payment gateway removed' });
      } else {
        if (!apiKey && !config?.keyConfigured) {
          return setMessage({ type: 'error', text: 'Please enter your API key' });
        }
        const payload = { gateway: selectedGateway, isLive };
        if (apiKey) payload.apiKey = apiKey;
        await axios.post(`${API}/admin/settings/payment`, payload, { headers: getAuthHeaders() });
        setApiKey('');
        setMessage({ type: 'success', text: 'Payment gateway saved successfully' });
      }
      fetchConfig();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save payment settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      await axios.post(`${API}/admin/settings/payment/test`, {}, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: '✅ Connection successful! Your API key is working.' });
      fetchConfig();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-gray-400">Loading...</div>;

  return (
    <div className="max-w-lg space-y-6">
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" /> : <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Gateway selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Payment Provider</label>
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              id: 'mollie',
              label: 'Mollie',
              desc: 'iDEAL, cards, SEPA',
              logo: '💳'
            },
            {
              id: 'stripe',
              label: 'Stripe',
              desc: 'Cards, wallets, SEPA',
              logo: '⚡'
            },
            {
              id: null,
              label: 'None',
              desc: 'No online payments',
              logo: '🚫'
            }
          ].map(gw => (
            <button
              key={String(gw.id)}
              onClick={() => setSelectedGateway(gw.id)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                selectedGateway === gw.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="text-2xl mb-2">{gw.logo}</div>
              <div className="font-medium text-sm text-gray-900">{gw.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{gw.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* API Key field — only shown if gateway selected */}
      {selectedGateway && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {selectedGateway === 'mollie' ? 'Mollie' : 'Stripe'} API Key
            </label>

            {/* Show existing masked key */}
            {config?.keyConfigured && !apiKey && (
              <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 rounded-lg text-sm text-green-700">
                <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                <span>Key configured: <code className="font-mono">{config.maskedKey}</code></span>
                {config.lastTested && (
                  <span className="text-xs text-green-500 ml-auto">
                    Tested {new Date(config.lastTested).toLocaleDateString('en-GB')}
                  </span>
                )}
              </div>
            )}

            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={config?.keyConfigured ? 'Enter new key to replace existing...' : `Paste your ${selectedGateway} API key...`}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              </button>
            </div>

            {selectedGateway === 'mollie' && (
              <p className="text-xs text-gray-400 mt-1">
                Find your API key at <a href="https://my.mollie.com/dashboard/developers/api-keys" target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">my.mollie.com</a>
              </p>
            )}
            {selectedGateway === 'stripe' && (
              <p className="text-xs text-gray-400 mt-1">
                Find your API key at <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">dashboard.stripe.com</a>
              </p>
            )}
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div
                onClick={() => setIsLive(!isLive)}
                className={`relative w-11 h-6 rounded-full transition-colors ${isLive ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${isLive ? 'translate-x-5' : 'translate-x-0'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {isLive ? '🟢 Live mode' : '🟡 Test / Sandbox mode'}
              </span>
            </label>
            <p className="text-xs text-gray-400 mt-1">
              {isLive ? 'Real payments will be processed' : 'No real payments — safe for testing'}
            </p>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? 'Saving...' : 'Save Payment Settings'}
        </button>
        {config?.keyConfigured && selectedGateway && (
          <button
            onClick={handleTest}
            disabled={testing}
            className="border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Settings Component
───────────────────────────────────────────── */
export default function Settings() {
  const [activeTab, setActiveTab] = useState('general');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your studio settings and integrations</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && <GeneralTab />}
      {activeTab === 'payment' && <PaymentGatewayTab />}
    </div>
  );
}
